import { AxiosRequestConfig } from "axios"
import { defineStore } from "pinia"
import { useAuthStore } from "src/stores/authStore"
import { DEBOUNCE_TIME_API_CALL } from "src/util/constants/configurations"

export interface ApiCallStoreType {
  endpointLastCall: Record<string, number>
  workspaceLastCall: Record<string, number>
}

export const useApiCallStore = defineStore("pinia-apiCallStore", {
  persist: true,

  state: (): ApiCallStoreType => {
    return {
      endpointLastCall: {},
      workspaceLastCall: {},
    }
  },
  getters: {
    getFixedUrl: () => {
      const authStore = useAuthStore()
      const uuid = authStore.selectedWorkspace?.id
      return (url: string) => (uuid && url.startsWith("/" + uuid) ? url : "/" + uuid + url)
    },
  },
  actions: {
    // Save the last get API request time in milliseconds in the store
    updateLastApiCallTime(config: AxiosRequestConfig, isSpringboardCall: boolean = false) {
      // We need to have some URL and we want to only look at GET requests
      if (!config.baseURL || !config.url || config.method !== "get") return

      const url = new URL(config.baseURL + config.url)
      const searchParams = new URLSearchParams(url.search)

      const ignoreList = ["page", "page_size", "updated_dt__gte"]

      for (const param of ignoreList) {
        searchParams.delete(param)
      }

      const urlString = url.pathname + searchParams.toString()
      const fixedUrl = isSpringboardCall ? urlString : this.getFixedUrl(urlString)

      // verify whether the url has a question mark except at the end
      const hasQuestionMark = fixedUrl.slice(0, -1).includes("?")

      // consider only the requests without any filtering - aka, there is no "?"
      if (!hasQuestionMark) {
        this.endpointLastCall[fixedUrl] = Date.now()
      }
    },

    updateLastApiCallTimeConcurrent(url: string) {
      const fixedUrl = this.getFixedUrl(url)
      this.endpointLastCall[fixedUrl] = Date.now()
    },

    // Takes the axios baseUrl
    updateWorkspaceLastApiCallTime(baseUrl?: string) {
      const uuid = baseUrl?.split("/").pop()
      if (uuid) {
        this.workspaceLastCall[uuid] = Date.now()
      }
    },

    canCallAgain(url: string, debounceTime?: number) {
      const fixedUrl = this.getFixedUrl(url)
      const lastCallTime = this.endpointLastCall[fixedUrl]

      // if the url is not stored in the store, call the API right away
      if (!lastCallTime) return true

      if (debounceTime === undefined) debounceTime = DEBOUNCE_TIME_API_CALL

      return Date.now() - lastCallTime > debounceTime
    },

    // Return the last API call UTC time stamp in the format: 2023-01-17T21:58:48.658Z
    getLastUpdatedTimeStamp(url: string) {
      const fixedUrl = this.getFixedUrl(url)
      const lastCallTime = this.endpointLastCall[fixedUrl]

      return lastCallTime ? new Date(lastCallTime).toISOString() : undefined
    },
  },
})
